/* eslint-disable no-restricted-globals */
import { format, isThisWeek, isThisYear, isToday, isYesterday, parse, parseISO } from 'date-fns';
import { enAU, enUS } from 'date-fns/locale';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { decode } from 'jsonwebtoken';
import * as Sentry from '@sentry/react';
import config from '../config.general';
import {
	auCulture,
	colorPalette,
	defaultCurrency,
	julyMonthIndex,
	localeCurrencies,
	usCulture
} from '../core/constants';

import { Director, Office } from '../models/officeAndDirectors';
import { DropDownListOption } from '../models/DropDownListOption';
import { LabelValueOption } from '../models/LabelValueOption';
import { InvestmentAccount } from '../models/InvestorModels';
import { UserOfficeCountries } from '../core/enums';
import { getAppUserOfficeCountryFromLocalStorage } from '../core/localStorage';
import { localizedWords } from './localizedWords';
import { hasValue } from './lib';
import { brandingColourHeavy } from '../appComponents/cssConstants';

/**
 * Get a localized word or return the key if not found.
 * Uses device's culture/locale
 * @param key The word to localize
 * @returns The localized word or the original key if not found
 */
export const getLocalizedWord = (key: string): string => {
	const deviceCulture = getCultureFromUserOfficeCountry();

	const regionWords = localizedWords[deviceCulture];
	if (!regionWords) {
		return localizedWords[auCulture][key] || key;
	}

	return regionWords[key] || key;
};

export const emailValidationRegex = /^\S+@\S+\.\S+$/;
export const websiteLinkRegex = /((https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})?(\/[^\s]*)?)/i;

export const createQueryString = (dictionary: Record<string, unknown>) => {
	const strings = Object.keys(dictionary)
		.map((key) => {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const value = (dictionary as any)[key];
			if (value === undefined || value === null) {
				return null;
			}
			return `${key}=${encodeURIComponent(value)}`;
		})
		.filter((x) => x !== null);
	return strings.reduce((previous, current) => previous + (previous === '?' ? '' : '&') + current, '?');
};

export const addQueryParam = (partialUrl: string, queryParams: { key: string; value: string }[]) => {
	// Add a temporary protocol to handle the partial URL
	const tempUrl = `http://dummy.com${partialUrl}`;
	const urlObject = new URL(tempUrl);

	queryParams.forEach((param) => {
		if (param.value) urlObject.searchParams.set(param.key, param.value);
	});

	// Removing the dummy protocol
	return urlObject.toString().replace('http://dummy.com', '');
};

export const pixelsToNumber = (pixels: string) => parseInt(pixels.slice(0, pixels.length - 2), 10);

// Attribution: https://stackoverflow.com/questions/43441856
export const scrollToRef = (ref: React.RefObject<{ offsetTop: number }>, offsetHeight = 0) =>
	window.scrollTo({
		left: 0,
		top: ref.current.offsetTop - offsetHeight, // '- offsetHeight' is for possible page global header
		behavior: 'smooth'
	});

export const scrollToPageTop = () =>
	window.scrollTo({
		left: 0,
		top: 0,
		behavior: 'smooth'
	});

export const isNonEmptyObject = (value: unknown) => {
	return value !== undefined && value !== null && Object.entries(value).length > 0;
};

export const isEqualIgnoreCase = (value1: string, value2: string) => value1?.toLowerCase() === value2?.toLowerCase();

export const formatCurrency = (value: number, decimalPlaces: number, currencySymbol: string, currencyCode: string) =>
	`${currencySymbol ?? currencyCode ?? '$'}${value
		.toFixed(decimalPlaces ?? 2)
		.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}`;

// Remove both the + and spaces from the phone number
export const getWhatsAppPhone = (mobileNumber: string) => mobileNumber.replace(/[+\s]/g, '');

export const getDropdownOptions = (items: string[]): DropDownListOption<string>[] =>
	items.sort().map((x) => {
		return { text: x, value: x };
	});

export const getUnsortedDropdownOptions = (items: string[]): DropDownListOption<string>[] =>
	items.map((x) => {
		return { text: x, value: x };
	});

export const getMarks = () => {
	const marks: { value: number; label: string }[] = [];
	for (let i = 1; i < 11; i++) {
		marks.push({ value: i, label: i.toString() });
	}
	return marks;
};

const parseAsLocalDateTime = (value: string): Date | null => {
	if (!value) return null;

	try {
		return parseISO(value);
	} catch {
		return null;
	}
};

/**
 * This method handles:
 * - A full ISO string with timezone: `'2025-02-05T05:28:26.4387755+00:00'`
 *   → Parses to corresponding local time.
 * - A date-only string: `'2025-05-07'`
 *   → Treated as midnight (00:00:00) local time on that date.
 */
export const formatDateWithTime = (value: string, stringFormat: string) => {
	if (!value) return null;

	const parsedDate = parseAsLocalDateTime(value);
	return format(parsedDate, stringFormat);
};

export const formatDate = (value: string) => (value ? format(new Date(value), getLocalizedWord('dateFormat')) : null);
export const formatTime = (timeString: string, stringFormat: string) => {
	const date = parse(timeString, 'HH:mm:ss', new Date());
	return format(date, stringFormat);
};

export const convertToDropDownList = (options: string[]) =>
	options.map((o) => {
		return { text: o, value: o };
	});

export const formatDecimalNumber = (value: number) => Number(value?.toFixed(2));

export const formatcurrency = (value: number | bigint, minimumFractionDigits = 0) =>
	new Intl.NumberFormat(getCultureFromUserOfficeCountry(), {
		style: 'currency',
		currency: localeCurrencies.find((c) => c.key === getCultureFromUserOfficeCountry())?.value ?? defaultCurrency,
		minimumFractionDigits,
		maximumFractionDigits: minimumFractionDigits
	}).format(value);

export const formatNumberWithComma = (value: number) => {
	if (value?.toString().endsWith('.')) {
		return value;
	}

	return new Intl.NumberFormat(getCultureFromUserOfficeCountry(), {
		minimumFractionDigits: 0
	}).format(value);
};

export const formatWholeNumber = (value: number) => {
	if (value === null || value === undefined || !Number.isFinite(value)) {
		return '';
	}

	return new Intl.NumberFormat(getCultureFromUserOfficeCountry(), {
		maximumFractionDigits: 0
	}).format(Math.round(value));
};

// TODO: need to consider to handle all supported offices countries user locale for Date field
export const getCurrentLocaleFromUserOfficeCountry = () => {
	switch (getUserOfficeCountry()) {
		case UserOfficeCountries.US:
			return enUS;
		case UserOfficeCountries.AU:
		default:
			return enAU;
	}
};

export const getUserOfficeCountry = (): UserOfficeCountries => {
	// For logged in users, useDirectorContactDetails will set the user office country to localStorage
	// For non-logged in users during registration, it will be set by OfficePartnerForm when they select their country/office
	const userCountry = getAppUserOfficeCountryFromLocalStorage();

	// Return the stored country or default to AU
	return (userCountry as UserOfficeCountries) ?? UserOfficeCountries.AU;
};

export const getCultureFromUserOfficeCountry = () =>
	getUserOfficeCountry() === UserOfficeCountries.US ? usCulture : auCulture;

// TODO: check if can replace fns from react-device-detect with the following Capacitor fns.
export const isWebApp = Capacitor.getPlatform() === 'web';
export const isIosApp = Capacitor.getPlatform() === 'ios';
export const isAndroidApp = Capacitor.getPlatform() === 'android';

/**
 * Get the app version from the native platform (iOS/Android) or from the general config (config.general.ts) for web.
 * Memoised — called on every API request, and the version is immutable per session.
 */
let cachedAppVersion: string | undefined;
export const getAppVersion = async (): Promise<string> => {
	if (cachedAppVersion) return cachedAppVersion;
	if (isWebApp) {
		cachedAppVersion = config.appVersion;
		return cachedAppVersion;
	}
	try {
		const appInfo = await App.getInfo();
		cachedAppVersion = appInfo.version;
	} catch {
		cachedAppVersion = config.appVersion;
	}
	return cachedAppVersion;
};

export const isLastActionWithinTargetHours = (lastActionTime: number, targetHours: number) => {
	if (!lastActionTime) return false;

	const currentTimeInMilliseconds = Date.now();
	const timeDifference = currentTimeInMilliseconds - lastActionTime;
	const hoursDifference = timeDifference / (1000 * 60 * 60);

	return hoursDifference <= targetHours;
};

export const isTokenExpiredOrAboutToExpire = (token: string) => {
	if (!token) return true; // Treat missing token as expired

	const decodedToken = decode(token) as { exp: number } | null;
	if (!decodedToken || !decodedToken.exp) {
		return true; // Treat invalid/malformed token as expired
	}

	const exp = decodedToken.exp * 1000;
	const bufferTime = 60000; // 1 minute in milliseconds
	return Date.now() > exp - bufferTime; // If the token has expired or is about to expire
};

// Some US offices use 'KellyPartners' in their location name
export const getOfficeNameByLocation = (location: string) =>
	location.includes('KellyPartners') ? location : `Kelly+Partners ${location}`;

export const getOfficesByCountry = (offices: Office[], country: string) => {
	if (!hasValue(country) || !hasValue(offices) || offices.length === 0) return [];

	const filteredOffices = offices.filter((office) => office.country === country);
	return filteredOffices.map((x) => ({ text: x.location, value: x.officeId }));
};

export const getOfficeLocationData = (offices: Office[]): DropDownListOption<number>[] => {
	if (!hasValue(offices) || offices.length === 0) return [];

	return offices.map((x) => {
		return { text: x.location, value: x.officeId };
	});
};

export const getDirectorsData = (directors: Director[], selectedOfficeId: number): DropDownListOption<number>[] => {
	if (!hasValue(selectedOfficeId) || selectedOfficeId < 1) return [];
	if (!hasValue(directors) || directors.length === 0) return [];

	return directors
		.map((d) => {
			if (d.officeId === selectedOfficeId) return { text: d.name, value: d.directorId };
			return null;
		})
		.filter((d) => d !== null);
};

export const convertCamelCaseToLabel = (fieldName: string): string => {
	return fieldName
		.replace(/([A-Z])/g, ' $1') // Insert space before each capital letter
		.replace(/^./, (str) => str.toUpperCase())
		.trim();
};

export const convertDropDownToLabelValueOption = <T>(
	dropDownOptions: DropDownListOption<T>[]
): LabelValueOption<T>[] => {
	return dropDownOptions.map((option) => ({
		label: option.text,
		value: option.value
	}));
};

export const getColorShades = (colorName: string) => {
	const color = colorPalette.find((c) => c.name.toLowerCase() === colorName.toLowerCase());
	if (color) {
		if (colorName.toLowerCase() === 'blue') {
			return { light: brandingColourHeavy, dark: brandingColourHeavy };
		}
		return { light: color.light, dark: color.dark };
	}
	return { light: null, dark: null }; // Return null or default colors if not found
};

export const capitalizeFirstLetter = (str: string): string => {
	if (!str) return str;
	return str.charAt(0).toUpperCase() + str.slice(1);
};

export function getFormattedDate(date: Date): string {
	if (isToday(date)) {
		return format(date, 'hh:mm a');
	}

	if (isYesterday(date)) {
		return 'Yesterday';
	}

	if (isThisWeek(date, { weekStartsOn: 1 })) {
		return format(date, 'EEEE');
	}

	if (isThisYear(date)) {
		return format(date, 'MMM d');
	}

	return format(date, 'MMM d, yyyy');
}

export const formatPostedTime = (postedTimeInUtc: string): string => {
	const time = new Date(postedTimeInUtc);
	const now = new Date();
	const diff = now.getTime() - time.getTime();
	const seconds = Math.floor(diff / 1000);
	const minutes = Math.floor(seconds / 60);
	const hours = Math.floor(minutes / 60);
	const days = Math.floor(hours / 24);

	if (seconds < 10) return 'Just now';
	if (seconds < 60) return `${seconds} seconds ago`;
	if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
	if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
	if (days < 7) return `${days} ${days === 1 ? 'day' : 'days'} ago`;
	return time.getFullYear() === now.getFullYear()
		? formatDateWithTime(postedTimeInUtc, 'd MMM')
		: formatDateWithTime(postedTimeInUtc, 'd MMM yyyy');
};

export const getFileNameWithoutExtension = (fileName: string): string => {
	return fileName.split('.').slice(0, -1).join('.');
};

export const getFileNameExtension = (fileName: string): string => {
	const parts = fileName.split('.');
	if (parts.length <= 1) {
		return '';
	}
	return parts[parts.length - 1].toUpperCase();
};

export const getNameFromEmail = (email: string): string => {
	const namePart = email.split('@')[0];
	const nameWords = namePart.split(/[._]/);
	const fullName = nameWords.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');

	return fullName;
};

export const getUserLocalDatePartOnlyInIso = (date: Date = new Date()) => {
	return date.toLocaleDateString('en-CA'); // 'en-CA' ensures the format is 'YYYY-MM-DD'
};

export const normalizeTodayDate = (): Date => {
	const today = new Date();
	return new Date(today.getFullYear(), today.getMonth(), today.getDate());
};

export const formatFileSize = (fileSizeInBytes: number): string => {
	const units = ['Bytes', 'KB', 'MB', 'GB'];
	let size = fileSizeInBytes;
	let unitIndex = 0;

	while (size >= 1024 && unitIndex < units.length - 1) {
		size /= 1024;
		unitIndex++;
	}

	return `${size.toFixed(2)} ${units[unitIndex]}`;
};

export const currentMonthYear = format(new Date(), 'MMMM yyyy');
export const isPushNotificationAvailable = Capacitor.isPluginAvailable('PushNotifications');

export const formatEventTime = (startTime: string, endTime: string, eventSessionTimeFormat: string): string => {
	return startTime === '23:59:59' && endTime === '23:59:59' // event end time
		? 'Event End'
		: `${formatTime(startTime, eventSessionTimeFormat)} - ${formatTime(endTime, eventSessionTimeFormat)}`;
};

export const getAuFinancialYear = () => {
	const today = new Date();
	const currentMonth = today.getMonth();
	const currentYear = today.getFullYear();

	// In JavaScript, months are 0-indexed
	const isAfterJune = currentMonth >= julyMonthIndex; // July (6) to December (11)
	return isAfterJune ? `${currentYear}-${currentYear + 1}` : `${currentYear - 1}-${currentYear}`;
};

export const getInvestmentAccounts = (investmentAccounts: {
	succeeded?: boolean;
	response?: unknown;
}): InvestmentAccount[][] => {
	if (investmentAccounts?.succeeded === true && Array.isArray(investmentAccounts.response)) {
		return investmentAccounts.response;
	}
	return null;
};

export const captureError = (err: unknown, configureScope?: (scope: Sentry.Scope) => void) => {
	let errorToSend: Error;

	if (err instanceof Error) {
		errorToSend = err;
	} else if (typeof err === 'string') {
		errorToSend = new Error(err);
	} else {
		// Avoid huge or circular JSON – keep it simple and safe
		errorToSend = new Error('Non-Error exception captured');
	}

	Sentry.withScope((scope) => {
		if (configureScope) configureScope(scope);

		// Optionally keep raw error as extra, but be careful with size
		if (err && typeof err === 'object') {
			scope.setExtra('rawErrorType', Object.prototype.toString.call(err));
		}

		Sentry.captureException(errorToSend);
	});
};

export const getRecentMonthOptions = (startDate?: string, numberOfMonths: number = 5) => {
	const options: { month: number; year: number; label: string }[] = [];
	const currentDate = new Date();
	const minDate = startDate ? new Date(startDate) : null;

	for (let i = 0; i < numberOfMonths; i++) {
		const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);

		// If startDate is provided, skip months before the startDate
		if (!minDate || date >= new Date(minDate.getFullYear(), minDate.getMonth(), 1)) {
			const month = date.getMonth() + 1;
			const year = date.getFullYear();
			const label = date.toLocaleDateString(config.currentCulture, { month: 'long', year: 'numeric' });

			options.push({ month, year, label });
		}
	}

	return options;
};

export const formatLargePercentage = (value: number | null | undefined) => {
	if (value == null) return '';
	const absValue = Math.abs(value);
	if (absValue >= 1000) {
		return `${Math.round(absValue / 1000)}K%`;
	}
	return `${Math.round(absValue)}%`;
};

export const delay = (ms: number) =>
	new Promise<void>((resolve) => {
		setTimeout(() => resolve(), ms);
	});
